/*
 * Copyright (C) 2024 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { getUrl, Resource, ResourceFactory, ResourceUri } from '@ngxp/rest';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ContentType, HttpHeader, RequestOptions } from '../tech.model';
import { CreateResourceData, LinkRelationName, SaveResourceData } from './resource.model';

@Injectable({ providedIn: 'root' })
export class ResourceRepository<T extends Resource = Resource> {
  private readonly resourceFactory = inject(ResourceFactory);
  private readonly httpClient = inject(HttpClient);

  static readonly SEARCH_PARAM: string = 'searchBy';

  public createResource(createResourceData: CreateResourceData): Observable<T> {
    return this.resourceFactory.from(createResourceData.resource).post(createResourceData.linkRel, createResourceData.toCreate);
  }

  public getResource<T>(uri: ResourceUri): Observable<T> {
    const uriWithoutParameter: string = this._getUrlWithoutParameter(uri);
    return this.resourceFactory.fromId(uriWithoutParameter).get();
  }

  _getUrlWithoutParameter(uri: ResourceUri): string {
    if (uri.includes('{')) {
      return uri.substring(0, uri.indexOf('{'));
    }
    return uri;
  }

  public save<T>(saveResourceData: SaveResourceData<Resource>): Observable<T> {
    return this.resourceFactory.from(saveResourceData.resource).put(saveResourceData.linkRel, saveResourceData.toSave);
  }

  public patch<T>(patchResourceData: SaveResourceData<Resource>): Observable<T> {
    return this.httpClient
      .patch<
        HttpResponse<T>
      >(getUrl(patchResourceData.resource, patchResourceData.linkRel), patchResourceData.toSave, this.buildPatchRequestOptions())
      .pipe(map((response: HttpResponse<T>) => response.body));
  }

  public mergePatch<T>(saveResourceData: SaveResourceData<Resource>): Observable<T> {
    return this.httpClient
      .patch<
        HttpResponse<T>
      >(getUrl(saveResourceData.resource, saveResourceData.linkRel), saveResourceData.toSave, this.buildMergePatchRequestOptions())
      .pipe(map((response: HttpResponse<T>) => response.body));
  }

  private buildPatchRequestOptions(): RequestOptions {
    return this.buildRequestOptions(ContentType.APPLICATION_JSON_PATCH);
  }

  private buildMergePatchRequestOptions(): RequestOptions {
    return this.buildRequestOptions(ContentType.APPLICATION_MERGE_PATCH_JSON);
  }

  private buildRequestOptions(contentType: ContentType): RequestOptions {
    let headers: HttpHeaders = new HttpHeaders();
    headers = headers.set(HttpHeader.CONTENT_TYPE, contentType);
    return <RequestOptions>{ headers, responseType: 'json', observe: 'response' };
  }

  public delete<T>(resource: Resource, linkRel: LinkRelationName): Observable<T> {
    return this.resourceFactory.from(resource).delete<T>(linkRel);
  }

  public search<T>(resource: Resource, linkRel: LinkRelationName, searchBy: string): Observable<T> {
    return this.resourceFactory.fromId(this._buildSearchUri(new URL(getUrl(resource, linkRel)), searchBy)).get();
  }

  _buildSearchUri(url: URL, searchBy: string): ResourceUri {
    url.searchParams.set(ResourceRepository.SEARCH_PARAM, searchBy);
    return url.href;
  }
}
